package features.auth

import features.booking.SmartVanColors
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.painterResource
import abi.composeapp.generated.resources.Res
import abi.composeapp.generated.resources.logotipo_smartvan

@Composable
fun LoginScreen(
    viewModel: AuthViewModel,
    onLoginSuccess: (String) -> Unit
) {
    val email by viewModel.email.collectAsState()
    val password by viewModel.password.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val errorMessage by viewModel.errorMessage.collectAsState()
    val roleAssigned by viewModel.roleAssigned.collectAsState()
    val requiereCambio by viewModel.requiereCambio.collectAsState()
    val cambioError by viewModel.cambioError.collectAsState()
    var passwordVisible by remember { mutableStateOf(false) }

    val smartvanDark = SmartVanColors.AzulOscuro
    val smartvanCyan = SmartVanColors.CyanAccent

    LaunchedEffect(roleAssigned) {
        if (roleAssigned != null) {
            onLoginSuccess(roleAssigned!!)
        }
    }

    // Forced password change dialog
    if (requiereCambio) {
        CambiarContrasenaDialog(
            isLoading = isLoading,
            errorMessage = cambioError,
            onCambiar = { nueva -> viewModel.cambiarContrasena(nueva) }
        )
        return
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(SmartVanColors.FondoClaro),
        contentAlignment = Alignment.Center
    ) {
        // Top accent bar
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(280.dp)
                .align(Alignment.TopCenter)
                .background(
                    Brush.verticalGradient(
                        colors = listOf(smartvanCyan.copy(alpha = 0.08f), Color.Transparent)
                    )
                )
        )

        // Subtle decorative circle
        Box(
            modifier = Modifier
                .size(400.dp)
                .offset(x = 180.dp, y = (-200).dp)
                .background(smartvanCyan.copy(alpha = 0.04f), CircleShape)
        )

        Card(
            modifier = Modifier
                .widthIn(max = 400.dp)
                .padding(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
            shape = RoundedCornerShape(20.dp)
        ) {
            Column(
                modifier = Modifier.padding(32.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                // Logo on white with subtle border
                Box(
                    modifier = Modifier
                        .padding(bottom = 8.dp)
                        .background(Color.White, CircleShape),
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(Res.drawable.logotipo_smartvan),
                        contentDescription = "SmartVan Logo",
                        modifier = Modifier.size(width = 140.dp, height = 90.dp)
                    )
                }

                Text(
                    text = "Bienvenido",
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = smartvanDark
                )

                Text(
                    text = "Inicia sesión para continuar",
                    color = Color.Gray,
                    style = MaterialTheme.typography.bodySmall
                )

                Spacer(modifier = Modifier.height(16.dp))

                // Email field
                OutlinedTextField(
                    value = email,
                    onValueChange = { viewModel.email.value = it },
                    label = { Text("Correo Electrónico") },
                    leadingIcon = {
                        Icon(Icons.Filled.Email, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(20.dp))
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = smartvanCyan,
                        unfocusedBorderColor = SmartVanColors.Borde,
                        focusedLabelColor = smartvanCyan,
                        unfocusedContainerColor = Color(0xFFFAFAFA),
                        focusedContainerColor = Color.White,
                        cursorColor = smartvanCyan
                    )
                )

                Spacer(modifier = Modifier.height(4.dp))

                // Password field
                OutlinedTextField(
                    value = password,
                    onValueChange = { viewModel.password.value = it },
                    label = { Text("Contraseña") },
                    leadingIcon = {
                        Icon(Icons.Filled.Lock, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(20.dp))
                    },
                    trailingIcon = {
                        IconButton(onClick = { passwordVisible = !passwordVisible }) {
                            Icon(
                                if (passwordVisible) Icons.Filled.VisibilityOff else Icons.Filled.Visibility,
                                contentDescription = if (passwordVisible) "Ocultar" else "Mostrar",
                                tint = Color.Gray,
                                modifier = Modifier.size(20.dp)
                            )
                        }
                    },
                    visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = smartvanCyan,
                        unfocusedBorderColor = SmartVanColors.Borde,
                        focusedLabelColor = smartvanCyan,
                        unfocusedContainerColor = Color(0xFFFAFAFA),
                        focusedContainerColor = Color.White,
                        cursorColor = smartvanCyan
                    )
                )

                // Error message
                if (errorMessage != null) {
                    Spacer(modifier = Modifier.height(4.dp))
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(
                            errorMessage!!,
                            color = Color.Red,
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium,
                            modifier = Modifier.padding(12.dp),
                            textAlign = TextAlign.Center
                        )
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                // Login button
                Button(
                    onClick = { viewModel.login() },
                    modifier = Modifier.fillMaxWidth().height(52.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = smartvanCyan,
                        disabledContainerColor = smartvanCyan.copy(alpha = 0.4f)
                    ),
                    shape = RoundedCornerShape(12.dp),
                    enabled = !isLoading && email.isNotBlank() && password.isNotBlank()
                ) {
                    if (isLoading) {
                        CircularProgressIndicator(
                            color = Color.White,
                            modifier = Modifier.size(22.dp),
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text(
                            "Iniciar Sesión",
                            fontWeight = FontWeight.Bold,
                            color = Color.White,
                            fontSize = 15.sp
                        )
                    }
                }

                Spacer(modifier = Modifier.height(4.dp))

                // Footer
                Text(
                    text = "Acceso exclusivo para personal autorizado",
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.LightGray,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}

@Composable
fun CambiarContrasenaDialog(
    isLoading: Boolean,
    errorMessage: String?,
    onCambiar: (String) -> Unit
) {
    var nueva by remember { mutableStateOf("") }
    var confirmar by remember { mutableStateOf("") }
    var mostrarNueva by remember { mutableStateOf(false) }
    var mostrarConfirmar by remember { mutableStateOf(false) }

    val smartvanDark = SmartVanColors.AzulOscuro
    val smartvanCyan = SmartVanColors.CyanAccent
    val coinciden = nueva == confirmar && nueva.length >= 6

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(SmartVanColors.FondoClaro),
        contentAlignment = Alignment.Center
    ) {
        Card(
            modifier = Modifier
                .widthIn(max = 420.dp)
                .padding(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
            shape = RoundedCornerShape(20.dp)
        ) {
            Column(
                modifier = Modifier.padding(28.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(56.dp)
                        .background(SmartVanColors.Naranja.copy(alpha = 0.1f), CircleShape),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(Icons.Filled.Lock, contentDescription = null, tint = SmartVanColors.Naranja, modifier = Modifier.size(28.dp))
                }

                Text(
                    "Cambio de Contraseña",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = smartvanDark
                )

                Text(
                    "Tu contraseña fue restablecida por un administrador. Debes establecer una nueva para continuar.",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.Gray,
                    textAlign = TextAlign.Center
                )

                Spacer(modifier = Modifier.height(12.dp))

                OutlinedTextField(
                    value = nueva,
                    onValueChange = { nueva = it },
                    label = { Text("Nueva Contraseña") },
                    leadingIcon = { Icon(Icons.Filled.Lock, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(20.dp)) },
                    trailingIcon = {
                        IconButton(onClick = { mostrarNueva = !mostrarNueva }) {
                            Icon(
                                if (mostrarNueva) Icons.Filled.VisibilityOff else Icons.Filled.Visibility,
                                contentDescription = null, tint = Color.Gray, modifier = Modifier.size(20.dp)
                            )
                        }
                    },
                    visualTransformation = if (mostrarNueva) VisualTransformation.None else PasswordVisualTransformation(),
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = smartvanCyan,
                        unfocusedBorderColor = SmartVanColors.Borde,
                        focusedLabelColor = smartvanCyan,
                        cursorColor = smartvanCyan
                    )
                )

                OutlinedTextField(
                    value = confirmar,
                    onValueChange = { confirmar = it },
                    label = { Text("Confirmar Contraseña") },
                    leadingIcon = { Icon(Icons.Filled.Lock, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(20.dp)) },
                    trailingIcon = {
                        IconButton(onClick = { mostrarConfirmar = !mostrarConfirmar }) {
                            Icon(
                                if (mostrarConfirmar) Icons.Filled.VisibilityOff else Icons.Filled.Visibility,
                                contentDescription = null, tint = Color.Gray, modifier = Modifier.size(20.dp)
                            )
                        }
                    },
                    visualTransformation = if (mostrarConfirmar) VisualTransformation.None else PasswordVisualTransformation(),
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(12.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = if (confirmar.isNotBlank() && !coinciden) Color.Red else smartvanCyan,
                        unfocusedBorderColor = if (confirmar.isNotBlank() && !coinciden) Color.Red else SmartVanColors.Borde,
                        focusedLabelColor = if (confirmar.isNotBlank() && !coinciden) Color.Red else smartvanCyan,
                        cursorColor = smartvanCyan
                    )
                )

                // Validation hints
                if (nueva.isNotBlank() && nueva.length < 6) {
                    Text("Mínimo 6 caracteres", color = Color.Red, style = MaterialTheme.typography.labelSmall)
                }
                if (confirmar.isNotBlank() && nueva != confirmar) {
                    Text("Las contraseñas no coinciden", color = Color.Red, style = MaterialTheme.typography.labelSmall)
                }

                if (errorMessage != null) {
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoRojo),
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(errorMessage, color = Color.Red, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(10.dp), textAlign = TextAlign.Center)
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                Button(
                    onClick = { onCambiar(nueva) },
                    modifier = Modifier.fillMaxWidth().height(50.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = smartvanCyan,
                        disabledContainerColor = smartvanCyan.copy(alpha = 0.3f)
                    ),
                    shape = RoundedCornerShape(12.dp),
                    enabled = coinciden && !isLoading
                ) {
                    if (isLoading) {
                        CircularProgressIndicator(color = Color.White, modifier = Modifier.size(22.dp), strokeWidth = 2.dp)
                    } else {
                        Text("Establecer Nueva Contraseña", fontWeight = FontWeight.Bold, color = Color.White)
                    }
                }
            }
        }
    }
}
